
import { Component, Vue, Watch, } from 'vue-property-decorator';
import { Address, AddressResult, } from '~/api/self-serve-client';
import WaterUsageTile from '~/apps/account/components/water-usage/water-usage-tile.vue';
import getDirectDebitAlertContent, { DirectDebitAlertContent, } from '~/apps/account/services/dashboard-service';
import { Results, Store, } from '~/apps/account/store';
import type { DashboardNotificationService, } from '~/apps/account/store/custom-content';
import FeedbackBox from '~/components/feedback-box/feedback-box.vue';
import HeaderText from '~/components/header-text/header-text.vue';
import LandingLink from '~/components/landing-link/landing-link.vue';
import Notifications from '~/components/notifications/notifications.vue';
import PropertyAddress from '~/components/property-address/property-address.vue';
import RippleBlock from '~/components/ripple-block/ripple-block.vue';
import DateDisplay from '~/components/self-serve/date-display/date-display.vue';
import Slider from '~/components/slider/slider.vue';
import Spinner from '~/components/spinner/spinner.vue';
import { dateDiff, } from '~/utils/dateFormatingMethods';
import { formattedMeterNumber, } from '~/utils/filters/formatedMeterNumber.filter';
import { toCurrency, } from '~/utils/filters/toCurrency.filter';
import { Completed, Result, isComplete, } from '~/utils/models/result';
import { getServiceName, } from '~/utils/service-name';
import DashboardTile from '../components/dashboard-tile/dashboard-tile.vue';

export interface AccountStatement {
	date: string;
	transaction: string;
	debit: string;
	credit: string;
	balance: string;
	archiveDocId?: string;
	archiveId?: string;
	viewBillIdentifier: string;
}

interface BillingDetailItem {
	billPeriod: string;
	viewLink: string;
	preBill: string | null;
	date: Date;
}

interface PaymentDuration {
	[key: string]: string;
}

@Component({
	components: {
		DateDisplay,
		DashboardTile,
		FeedbackBox,
		HeaderText,
		LandingLink,
		PropertyAddress,
		RippleBlock,
		Spinner,
		WaterUsageTile,
		Slider,
		Notifications,
	},
	metaInfo: {
		title: 'Your Account - Southern Water',
	},
})
export default class Dashboard extends Vue {
	customerNumber = Store.state.businessPartnerDetails?.number;
	contractAccountNumber = Store.state.selectContractAccount;
	paymentDuration: PaymentDuration = { monthly: 'month', weekly: 'week', fortnightly: 'fortnight', };
	dashboardPopupDateRange = Store.state.customContent.dashboardPopupDateRange;
	displayNotifications = {} as DashboardNotificationService;
	businessPartnerDetails = Store.state.businessPartnerDetails;

	get nextDirectDebitDate() {
		return Store.state.nextDirectDebitDate;
	}

	get welcomeMessage() {
		if (!Store.state.businessPartnerDetails) {
			return '';
		}
		return `Hi, ${Store.state.businessPartnerDetails.firstName} ${Store.state.businessPartnerDetails.lastName}`;
	}

	get noAssociatedPropertyFound() {
		const contractAccountList = Store.state.businessPartnerDetails?.contractAccounts;
		return !contractAccountList?.find((item) => item.number && item.isSouthEastWaterContract === false);
	}

	get recommendedAmount() {
		return Store.state.recommendedAmount as number;
	}

	get shouldCallNotification() {
		return !!((this.getPaymentMethod || 'C') && this.isPropertyActive && this.serviceProvided && this.getContractData);
	}

	get selectedContractAccountSmStatus() {
		return Store.state.selectedContractAccountSmStatus;
	}

	@Watch('shouldCallNotification')
	callNotification() {
		this.getNotification();
	}

	moveOutDays(param: Date | string) {
		return dateDiff(new Date(new Date().setHours(0, 0, 0, 0)), new Date(param));
	}

	mounted() {
		if (sessionStorage.getItem('firstVisitOnDahboard')) {
			void Store.dispatch.contractAccountDetail({ contractAccountNumber: this.selectedContractAccount, journeyName: 'MountedDashboard', });
		} else {
			sessionStorage.setItem('firstVisitOnDahboard', 'true');
		}
		void Store.dispatch.setCorrespondenceAddress(this.selectedContractAccount);
		void Store.dispatch.updateProfileDetails();
		void Store.commit.setSelectedWaterUsageDetails({
			meterID: '',
			meterCategory: '',
			contractId: '',
		});
		if (this.isNewContentDisplay()) {
			Store.commit.showOverlay(
				{
					message: '',
					isDisplayNewFeature: true,
				}
			);
		}
	}

	async getNotification() {
		try {
			const { result, } = await this.$selfServeApi.notificationGetUserNotificationList({
				isActiveCustomer: this.isPropertyActive as boolean | undefined,
				isCashCustomer: !this.getPaymentMethod as boolean | undefined,
				isUnmeterCustomer: !this.isMetered as boolean | undefined,
				isAssessedCustomer: this.isAssessed as boolean | undefined,
				isWaterCustomer: this.serviceProvided !== 'Wastewater',
				businessPartnerNumber: Number(this.customerNumber) || undefined,
				contractAccountNumber: this.contractAccountNumber || undefined,
				contractNumber: this.selectedContractAccountNumber || undefined,
			});
			const setNotificationsPayload: number[] = [];
			const notificationList = this.dashboardNotificationService.notificationList.filter((item) => item.id === result.find((id) => {
				if (item.id === id) {
					setNotificationsPayload.push(id);
					return item.id === id;
				}
			}));
			this.displayNotifications = { ...this.dashboardNotificationService, notificationList: notificationList, };
			if (result.length && setNotificationsPayload.length) {
				setNotificationsPayload.forEach(async (element) => {
					try {
						await this.$selfServeApi.notificationSetUserNotificationNotificationIdCreate({ notificationId: element, });
					} catch (error) {
						console.error(error);
					}
				});
			}
		} catch (error) {
			console.error(error);
		}
	}

	isNewContentDisplay() {
		const isValidFromDate = dateDiff(new Date(), new Date(this.dashboardPopupDateRange.FromDate)) <= 0;
		const isValidToDate = dateDiff(new Date(), new Date(this.dashboardPopupDateRange.ToDate)) >= 0;
		const sessionLoginCount = sessionStorage.getItem('sessionLoginCount');

		if (this.dashboardPopupItems.length && !sessionLoginCount && isValidFromDate && isValidToDate) {
			sessionStorage.setItem('sessionLoginCount', '1');
			return true;
		}
		return false;
	}

	get dashboardPopupItems() {
		return Store.state.customContent.dashboardPopupItems;
	}

	get dashboardNotificationService() {
		return Store.state.customContent.dashboardNotificationService;
	}

	get moveOutDate() {
		if (Store.state.contractData.state === 'completed' && Store.state.contractData.result.moveOutDate) {
			return Store.state.contractData.result.moveOutDate;
		}
		return null;
	}

	// Not used by any method or component
	// get contractId() {
	// 	if (Store.state.contractAccountDetails.state === 'completed') {
	// 		return Store.state.contractAccountDetails.result.contracts[0];
	// 	}
	// 	return '';
	// }

	get waterUsageDetails() {
		return Store.state.waterUsageDetails;
	}

	get canMoveOutOnDashboard() {
		return Store.getters.canMoveOutOnDashboard;
	}

	// get preferredPhoneNumber() {
	// 	if (this.profileDetails?.mobileNumber) {
	// 		return {
	// 			label: 'Mobile',
	// 			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
	// 			value: `${this.profileDetails.mobileIsdCode} (0) ${this.profileDetails.mobileNumber.replace(/^0+/, '')}`,
	// 		};
	// 	} else if (this.profileDetails?.telephoneNumber) {
	// 		return {
	// 			label: 'Telephone',
	// 			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
	// 			value: `${this.profileDetails.telephoneIsdCode} (0) ${this.profileDetails.telephoneNumber.replace(/^0+/, '')}`,
	// 		};
	// 	} else {
	// 		return null;
	// 	}
	// }

	get preferredPhoneNumber() {
		if (this.businessPartnerDetails?.mobileTelephoneNumber) {
			return {
				label: 'Mobile',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-call
				value: `${this.businessPartnerDetails.mobileIsd} (0) ${this.businessPartnerDetails.mobileTelephoneNumber.replace(/^0+/, '')}`,
			};
		} else if (this.businessPartnerDetails?.telephoneNumber) {
			return {
				label: 'Telephone',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-call
				value: `${this.businessPartnerDetails.telephoneIsd} (0) ${this.businessPartnerDetails.telephoneNumber.replace(/^0+/, '')}`,
			};
		} else {
			return null;
		}
	}

	get selectedContractAccount() {
		return Store.state.selectContractAccount;
	}

	get selectedContractAccountNumber() {
		return Store.state.selectedContractAccountNumber;
	}

	// get switchAccountDetails() {
	// 	if (Store.state.switchAccountDetails.state === 'completed') {
	// 		return Store.state.switchAccountDetails.result;
	// 	}
	// }

	// setPaymentReferenceToDisplay() {
	// 	const setPaymentRef = this.switchAccountDetails?.find((item) => item.contractAccountId === this.selectedContractAccount);
	// 	const result = setPaymentRef?.paymentReference ? setPaymentRef?.paymentReference : setPaymentRef?.contractAccountId;
	// 	return result;
	// }

	setPaymentReferenceToDisplay() {
		const result = this.contractAccountData?.paymentReferenceNumber ? this.contractAccountData.paymentReferenceNumber : this.selectedContractAccount;
		return result;
	}

	// get isMetered() {
	// 	return isComplete(this.contractAccountDetails) && this.contractAccountDetails.result.isMetered;
	// }

	get isMetered() {
		return isComplete(this.contractData) && this.contractData?.result.isMetered;
	}

	get isAssessed() {
		return isComplete(this.contractData) && this.contractData?.result.isAssessed;
	}

	get getContractData() {
		return isComplete(this.contractData) && this.contractData?.result;
	}

	get isToleranceLimitNull() {
		return Store.state.isToleranceLimitNull;
	}

	// get isPropertyActive() {
	// 	return Store.state.switchAccountDetails.state === 'completed' ? Store.state.switchAccountDetails.result.find((item) => item.conractAccountId === this.selectedContractAccount)?.isPropertyActive : null;
	// }

	get isPropertyActive() {
		return Store.state.contractData.state === 'completed' && Store.state.contractData.result.isMovedOut === false;
	}

	// get getPaymentMethod() {
	// 	if (this.contractAccountDetails.state === 'completed') {
	// 		return this.contractAccountDetails.result.paymentMethod;
	// 	}
	// }

	get getPaymentMethod() {
		if (this.contractAccountData?.paymentMethod) {
			return this.contractAccountData.paymentMethod;
		}
	}

	paymentMethod = 'C';

	// Need to remove below code once all new API are done
	// get contractAccountDetails(): Result<ContractAccountDetails> {
	// 	return Store.state.contractAccountDetails;
	// }

	get contractAccountData() {
		if (Store.state.contractAccountData.state === 'completed') {
			return Store.state.contractAccountData.result;
		}
	}

	get contractData() {
		return Store.state.contractData;
	}

	get contractAccountPaymentDetails() {
		return Store.state.contractAccountPaymentDetails;
	}

	get contractAccountDetailCompleted(): DirectDebitAlertContent {
		if (this.contractAccountPaymentDetails.state === 'completed') {
			return getDirectDebitAlertContent(this.isToleranceLimitNull, this.isMetered, this.recommendedAmount, this.getPaymentAmount as number, this.getPaymentFrequency as string);
		} else {
			return {} as DirectDebitAlertContent;
		}
	}

	// get getPaymentAmount() {
	// 	if (Store.state.contractAccountDetails.state === 'completed' && Store.state.contractAccountDetails.result.paymentScheduleItems[0].paymentAmount) {
	// 		return Store.state.contractAccountDetails.result.paymentScheduleItems[0].paymentAmount;
	// 	}
	// }

	get getPaymentAmount() {
		if (Store.state.contractAccountPaymentDetails.state === 'completed' && Store.state.contractAccountPaymentDetails.result.nextPaymentAmount) {
			return Store.state.contractAccountPaymentDetails.result.nextPaymentAmount;
		}
	}

	// get getPaymentFrequency() {
	// 	if (this.contractAccountDetails.state === 'completed' && this.contractAccountDetails.result.paymentFrequency) {
	// 		return this.contractAccountDetails.result.paymentFrequency;
	// 	}
	// 	return null;
	// }

	get getPaymentFrequency() {
		if (Store.state.contractAccountPaymentDetails.state === 'completed' && Store.state.contractAccountPaymentDetails.result.paymentFrequency) {
			return Store.state.contractAccountPaymentDetails.result.paymentFrequency;
		}
		return null;
	}

	// get paymentDueDate() {
	// 	if (this.contractAccountDetails.state === 'completed' && this.contractAccountDetails.result.paymentDueDate) {
	// 		const currentPaymentDueDate = this.contractAccountDetails.result.paymentDueDate;
	// 		return new Date(currentPaymentDueDate).setDate(new Date(currentPaymentDueDate).getDate() + 16);
	// 	}
	// }

	get paymentDueDate() {
		if (this.contractAccountPaymentDetails.state === 'completed' && this.contractAccountPaymentDetails.result.dueDate) {
			const currentPaymentDueDate = this.contractAccountPaymentDetails.result.dueDate;
			return new Date(currentPaymentDueDate).setDate(new Date(currentPaymentDueDate).getDate() + 16);
		}
	}

	// get contractAccountPaymentMethod() {
	// 	if (this.contractAccountDetails.state === 'completed' && this.contractAccountDetails.result.paymentMethod === 'D') {
	// 		const { contracts, isMetered, } = this.contractAccountDetails.result;
	// 		void Store.dispatch.getRecommendedAmount({ contractId: contracts[0], isMetered: isMetered, });
	// 		// this.getDirectDebitDate();
	// 		return null;
	// 	}
	// }

	get contractAccountPaymentMethod() {
		return this.contractAccountData?.paymentMethod === 'D' && this.contractData.state === 'completed';
	}

	@Watch('contractAccountPaymentMethod')
	shouldGetRecommendedAmount() {
		this.callToGetRecommendedAmount();
	}

	callToGetRecommendedAmount() {
		if (this.contractData.state === 'completed') {
			const { number, isMetered, } = this.contractData.result;
			void Store.dispatch.getRecommendedAmount({ contractId: number, isMetered: isMetered, });
			return null;
		}
	}

	// get paymentFrequencyForPaymentCard() {
	// 	if (this.contractAccountDetails.state === 'completed' && this.contractAccountDetails.result.paymentFrequency) {
	// 		return this.contractAccountDetails.result.paymentFrequency === 'EVERY 2 WEEKS' ? 'fortnightly' : this.contractAccountDetails.result.paymentFrequency.toLowerCase();
	// 	}
	// 	return null;
	// }

	get paymentFrequencyForPaymentCard() {
		if (this.contractAccountPaymentDetails.state === 'completed' && this.contractAccountPaymentDetails.result.paymentFrequency) {
			return this.contractAccountPaymentDetails.result.paymentFrequency === 'EVERY 2 WEEKS' ? 'fortnightly' : this.contractAccountPaymentDetails.result.paymentFrequency.toLowerCase();
		}
		return null;
	}

	// get customerProfile(): Result<AuthenticatedCustomerProfileDetails> {
	// 	return Store.state.customerProfile;
	// }

	get customerProfile(): Results<Address> | undefined {
		if (Store.state.correspondenceAddress.state === 'completed') {
			return Store.state.correspondenceAddress;
		}
	}

	// Below code is not used by any method or component
	// get profileDetails() {
	// 	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	// 	return this.customerProfile.state === 'completed' ? this.customerProfile.result : null;
	// }

	// get meterList() {
	// 	return Store.getters.meterList;
	// }

	get meterList() {
		return Store.getters.meterListV2;
	}

	// get serviceProvided() {
	// 	return Store.state.contractPropertyDetails.state === 'completed' ? getServiceName(Store.state.contractPropertyDetails.result.customerServiceFlag) : '';
	// }

	get serviceProvided() {
		return Store.state.contractMeterDetailsDetail.state === 'completed' && Store.state.contractMeterDetailsDetail.result.wocCustomer ? getServiceName(Store.state.contractMeterDetailsDetail.result.wocCustomer) : '';
	}

	// get contractPropertyDetails(): Result<Address> {
	// 	return Store.state.contractSupplyAddressDetail;
	// }

	get contractSupplyAddressDetail() {
		return Store.state.contractSupplyAddressDetail;
	}

	get accountStatement() {
		return Store.state.accountStatement;
	}

	get billingDetail(): Result<BillingDetailItem | null> {
		if (this.accountStatement.state !== 'completed') {
			return this.accountStatement;
		}

		if (this.contractAccountPaymentDetails.state === 'completed' && !this.contractAccountPaymentDetails.result.lastBillAmount) {
			return Completed(null);
		}

		const result = this.accountStatement.result.items.filter((item) => item.viewBillIdentifier);
		if (result.length < 1) {
			return Completed(null);
		}

		return Completed({
			billPeriod: result[0].transaction.replace('Charges for ', ''),
			preBill: result[1] ? result[1].debit : null,
			date: new Date(result[0].date),
			viewLink: `${Store.getters.getGlobalConfigValue('SelfServeApiAddress') || ''}${result[0].viewLink}`,
		});
	}

	// get ContractAccountMoveOutDate() {
	// 	if (this.contractAccountDetails.state === 'completed') {
	// 		return this.contractAccountDetails.result.moveOutDate ?? '';
	// 	}
	// 	return '';
	// }

	comparePropertyCorrespondenceAddress(correspondenceAddress: AddressResult | undefined) {
		const corresAddress = { ...(correspondenceAddress ?? {}), } as Record<string, string>;
		const PropAddress = (this.contractSupplyAddressDetail.state === 'completed' ? this.contractSupplyAddressDetail.result : {}) as Record<string, string>;

		// eslint-disable-next-line dot-notation
		delete corresAddress['region'];
		// eslint-disable-next-line dot-notation
		delete PropAddress['region'];

		const outPutOfCorresAddress = Object.keys(corresAddress).map((item) => corresAddress[item].trim().toLowerCase());
		const outPutOfPropAddress = Object.keys(PropAddress).map((item) => PropAddress[item].trim().toLowerCase());

		if (JSON.stringify(outPutOfCorresAddress) === JSON.stringify(outPutOfPropAddress)) {
			return true;
		}
		return false;
	}

	// reloadContractAccountDetails() {
	// 	if (this.selectedContractAccount) {
	// 		void Store.dispatch.updateContractAccountDetails(this.selectedContractAccount);
	// 	}
	// }

	reloadContractAccountDetails() {
		if (this.selectedContractAccount) {
			void Store.dispatch.contractAccountPaymentDetailsDetail({
				selectContractAccount: this.selectedContractAccount
			});
		}
	}

	// reloadMeterList() {
	// 	if (this.selectedContractAccount) {
	// 		void Store.dispatch.updateClientMeterList(this.selectedContractAccount);
	// 	}
	// }

	reloadMeterList() {
		if (this.selectedContractAccountNumber) {
			void Store.dispatch.contractMeterDetailsDetail({
				contractNumber: this.selectedContractAccountNumber
			});
		}
	}

	// reloadContractPropertyDetails() {
	// 	if (this.selectedContractAccount) {
	// 		void Store.dispatch.updateContractPropertyDetails(this.selectedContractAccount);
	// 	}
	// }

	reloadContractPropertyDetails() {
		if (this.selectedContractAccountNumber) {
			void Store.dispatch.contractSupplyAddressDetail({
				contractNumber: this.selectedContractAccountNumber,
				journeyName: 'ReloadDashboard',
			});
		}
	}

	// reloadCustomerProfile() {
	// 	void Store.dispatch.updateProfileDetails();
	// }

	reloadCustomerProfile() {
		void Store.dispatch.setCorrespondenceAddress(this.selectedContractAccount);
	}

	reloadBillingDetails() {
		if (this.selectedContractAccount) {
			void Store.dispatch.updateAccountStatement(this.selectedContractAccount);
		}
	}

	strugglingToPay = Store.getters.getGlobalConfigValue('StrugglingToPay');
	contactUsLocation = Store.getters.getGlobalConfigValue('ContactUsLocation');

	dashboardContentTiles = Store.state.customContent.dashboardContentTiles;

	formattedMeterNumber = formattedMeterNumber;
	toCurrency = toCurrency;

	paymentFrequency() {
		if (this.paymentFrequencyForPaymentCard !== null) {
			return this.paymentDuration[this.paymentFrequencyForPaymentCard];
		}
	}

	get extraHelpAndSupport() {
		return Store.getters.getGlobalConfigValue('ExtraHelpAndSupport');
	}

	get contractMeterDetailsDetail() {
		return Store.state.contractMeterDetailsDetail;
	}

	get meterReadsDetail() {
		return Store.state.meterReadsDetail.state === 'completed' ? Store.state.meterReadsDetail.result : null;
	}
}
